/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, March 26th 2024, 10:23:02 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */
import React from "react";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import DeleteIcon from "@mui/icons-material/Delete";
import { WorkerAccount } from "lib/schemas";
import { useAPIRemoveWorkerFromOrg } from "workers/services/WorkersAPI";
import { ActionDialog, useToggleState } from "ui-widgets";
import { useGlobalScope } from "core/providers";

interface RemoveWorkerFromOrgProps {
  workerId: WorkerAccount["id"];
  workerName: string;
  open: boolean;
  handleClose: () => void;
  handleUpdatedWorkerFromAPI?: (apiResponse: WorkerAccount) => void;
}

const RemoveWorkerFromOrg = ({
  workerId,
  workerName,
  handleClose,
  open,
  handleUpdatedWorkerFromAPI,
}: RemoveWorkerFromOrgProps) => {
  const {
    globalState: { selectedOrganization },
  } = useGlobalScope();
  const { deleteData } = useAPIRemoveWorkerFromOrg();

  const removeWorker = () => {
    deleteData({
      apiPathParams: [workerId],
      setValidAPIResponse: (apiResponse: WorkerAccount) => {
        handleUpdatedWorkerFromAPI?.(apiResponse);
      },
    });
    handleClose();
  };

  const warningTitle = `Remove ${workerName} from \`${selectedOrganization.name}\``;
  const warningText = (
    <>
      <p>
        You are about to remove {workerName} from your organization ({selectedOrganization.name}). They will no longer
        have access to your organization's resources on the Onestop platform.
      </p>
      <p>
        Their account will be archived and only be kept for historical purposes. The only way to let {workerName} back
        into your organization will be to invite them again.
      </p>
      <p>Are you sure about this?</p>
    </>
  );

  return (
    <>
      <ActionDialog
        severity="warning"
        title={warningTitle}
        open={open}
        onClose={handleClose}
        confirmationAction={removeWorker}
        dialogMessage={warningText}
      />
    </>
  );
};

interface RemoveWorkerFromOrgButtonWrapperProps
  extends Pick<RemoveWorkerFromOrgProps, "workerId" | "workerName" | "handleUpdatedWorkerFromAPI"> {
  children: any;
}

export const RemoveWorkerFromOrgButtonWrapper = ({
  children,
  workerId,
  workerName,
  handleUpdatedWorkerFromAPI,
}: RemoveWorkerFromOrgButtonWrapperProps) => {
  const { handleClose, handleOpen, open } = useToggleState();
  return (
    <>
      {React.Children.map(children, (child) => React.cloneElement(child, { onClick: handleOpen }))}
      <RemoveWorkerFromOrg
        handleClose={handleClose}
        open={open}
        workerId={workerId}
        workerName={workerName}
        handleUpdatedWorkerFromAPI={handleUpdatedWorkerFromAPI}
      />
    </>
  );
};

interface RemoveWorkerFromOrgButtonProps
  extends Pick<RemoveWorkerFromOrgButtonWrapperProps, "handleUpdatedWorkerFromAPI"> {
  worker: WorkerAccount;
}

export const RemoveWorkerFromOrgButton = ({ worker, handleUpdatedWorkerFromAPI }: RemoveWorkerFromOrgButtonProps) => {
  const {
    userContext: { loggedInUser },
    globalState: { selectedOrganization },
  } = useGlobalScope();
  const icon = <DeleteIcon color="error" />;
  const tooltipText = `Remove ${worker.first_name} from Organization \`${selectedOrganization.name}\``;

  const hideButton = loggedInUser?.id === worker.id || worker.is_super_user;

  return hideButton ? null : (
    <RemoveWorkerFromOrgButtonWrapper
      workerId={worker.id}
      workerName={worker.first_name}
      handleUpdatedWorkerFromAPI={handleUpdatedWorkerFromAPI}
    >
      <Tooltip title={<Typography>{tooltipText}</Typography>}>
        <IconButton>{icon}</IconButton>
      </Tooltip>
    </RemoveWorkerFromOrgButtonWrapper>
  );
};
